import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { systemApplicationFilters } from './system-application.filters';

// Segurança (U24): as duas telas saem do agente (`agent: false`) e nenhuma ação é `page: true`.

export const SYSTEM_APPLICATION_PATH = '/seguranca/usuarios-aplicacao';
export const systemApplicationPath = (id: string) =>
  `${SYSTEM_APPLICATION_PATH}/${encodeURIComponent(id)}`;

const READ = can('UserApplication', 'Read');
const ref = z.object({ id: z.string().min(1), name: z.string().optional() });
const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela de usuários de aplicação para executar esta ação.`);
};

export const systemApplicationCreate = action({
  id: 'systemApplication.create',
  kind: 'draft',
  label: msg`Nova aplicação`,
  description: msg`Abre o formulário em branco de usuário de aplicação. Nada é gravado até a pessoa salvar.`,
  permission: can('UserApplication', 'Create'),
  run: ({ nav }) => nav.to(systemApplicationPath('novo')),
});

export const systemApplicationOpen = action({
  id: 'systemApplication.open',
  kind: 'navigate',
  label: msg`Editar`,
  description: msg`Abre a ficha de um usuário de aplicação da lista. Nada muda só por abrir.`,
  row: true,
  input: ref,
  run: ({ nav, input }) => nav.to(systemApplicationPath(input.id)),
});

export const systemApplicationDelete = action({
  id: 'systemApplication.delete',
  kind: 'destructive',
  label: msg`Excluir`,
  description: msg`Exclui um usuário de aplicação: o sistema integrado por ele deixa de conseguir se autenticar. Pede a confirmação da pessoa.`,
  row: true,
  permission: can('UserApplication', 'Delete'),
  input: ref,
  preview: () => ({
    title: t`Excluir`,
    detail: t`Tem certeza que deseja excluir esta aplicação?`,
    tone: 'destructive',
  }),
  run: mountedScreenRequired,
});

export const systemApplicationListRefresh = action({
  id: 'systemApplication.list.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de usuários de aplicação. Não muda nada.`,
  permission: READ,
  run: mountedScreenRequired,
});

export const systemApplicationFilter = {
  ...filterAction('systemApplication.filter', systemApplicationFilters, {
    description: msg`Filtra os usuários de aplicação pelo nome.`,
  }),
  page: false,
};

export const systemApplicationListPage = {
  ...listPageAction('systemApplication.list.page'),
  page: false,
};

export const systemApplicationBack = action({
  id: 'systemApplication.back',
  kind: 'navigate',
  label: msg`Usuários de Aplicação`,
  description: msg`Volta para a lista de usuários de aplicação.`,
  run: ({ nav }) => nav.to(SYSTEM_APPLICATION_PATH),
});

export const systemApplicationRefresh = action({
  id: 'systemApplication.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados da aplicação aberta. Não muda nada.`,
  run: mountedScreenRequired,
});

export const systemApplicationHistory = action({
  id: 'systemApplication.history',
  kind: 'read',
  label: msg`Histórico`,
  description: msg`Abre o histórico de alterações da aplicação aberta. Não muda nada.`,
  run: mountedScreenRequired,
});

export const systemApplicationSave = action({
  id: 'systemApplication.save',
  kind: 'write',
  label: msg`Salvar`,
  description: msg`Confere e salva o formulário da aplicação aberta. Pede a confirmação da pessoa.`,
  preview: () => ({
    title: t`Salvar aplicação`,
    detail: t`Confira os dados da aplicação antes de salvar.`,
  }),
  run: mountedScreenRequired,
});

export const systemApplicationScopes = action({
  id: 'systemApplication.scopes',
  kind: 'draft',
  label: msg`Gerenciar escopos`,
  description: msg`Abre o painel de escopos da aplicação aberta, para cadastrar ou excluir escopos. Nada é gravado só por abrir.`,
  run: mountedScreenRequired,
});

export const systemApplicationDeleteScope = action({
  id: 'systemApplication.scope.delete',
  kind: 'destructive',
  label: msg`Excluir escopo`,
  description: msg`Exclui um escopo; outras aplicações que o usam deixam de se autenticar com ele. Pede a confirmação da pessoa.`,
  row: true,
  permission: can('ResourceServer', 'Update'),
  input: z.object({ resourceServerId: z.string().min(1), scopeName: z.string().min(1) }),
  preview: ({ scopeName }) => ({
    title: t`Excluir`,
    detail: t`Excluir o escopo ${scopeName}?`,
    tone: 'destructive',
  }),
  run: mountedScreenRequired,
});

export const systemApplicationDeleteResourceServer = action({
  id: 'systemApplication.resourceServer.delete',
  kind: 'destructive',
  label: msg`Excluir resource server`,
  description: msg`Exclui um resource server com todos os escopos dele; outras aplicações que os usam deixam de se autenticar. Pede a confirmação da pessoa.`,
  row: true,
  permission: can('ResourceServer', 'Delete'),
  input: z.object({ resourceServerId: z.string().min(1) }),
  preview: () => ({
    title: t`Excluir`,
    detail: t`Excluir resource server e todos os seus escopos`,
    tone: 'destructive',
  }),
  run: mountedScreenRequired,
});

const ACCESS = { rootTenancy: true } as const;

export const systemApplicationListScreen = defineScreen({
  id: 'systemApplication.list',
  path: SYSTEM_APPLICATION_PATH,
  title: msg`Usuários de Aplicação`,
  description: msg`Usuários de aplicação, as credenciais com que outros sistemas se integram à UY3, com filtro por nome, ordenação, criação, edição e exclusão.`,
  permission: READ,
  access: ACCESS,
  agent: false,
  place: { section: 'security', icon: 'bot' },
  actions: [
    systemApplicationCreate,
    systemApplicationListRefresh,
    systemApplicationFilter,
    systemApplicationListPage,
    systemApplicationOpen,
    systemApplicationDelete,
  ],
});

export const systemApplicationDetailScreen = defineScreen({
  id: 'systemApplication.detail',
  path: `${SYSTEM_APPLICATION_PATH}/$aplicacaoId`,
  title: msg`Aplicação`,
  description: msg`Cria ou edita um usuário de aplicação em cinco abas, com o painel de escopos, a janela das credenciais criadas e o histórico de alterações.`,
  permission: READ,
  access: ACCESS,
  agent: false,
  place: {
    parent: 'systemApplication.list',
    icon: 'file-text',
    record: msg`Aplicação`,
    tabs: [
      { id: 'dados', label: msg`Dados da aplicação` },
      { id: 'oauth', label: msg`OAuth e escopos` },
      { id: 'urls', label: msg`URLs de redirecionamento` },
      { id: 'autenticacao', label: msg`Autenticação e segurança` },
      { id: 'tokens', label: msg`Tokens` },
    ],
  },
  actions: [
    systemApplicationBack,
    systemApplicationHistory,
    systemApplicationRefresh,
    systemApplicationSave,
    systemApplicationScopes,
    systemApplicationDeleteScope,
    systemApplicationDeleteResourceServer,
  ],
});
